import { DateField } from '@/global/components/DateField'
import dayjs, { type Dayjs } from 'dayjs'
import 'dayjs/locale/pt-br'
import type { ReactNode } from 'react'
import {
    Controller,
    type UseControllerProps,
    useFormContext,
} from 'react-hook-form'
import { P, match } from 'ts-pattern'

type FormPickerProps = Omit<UseControllerProps, 'control' | 'defaultValue'> & {
    disabled?: boolean
    label?: string
    name: string
    required?: boolean
    size?: 'small' | 'medium'
    type?: 'date' | 'month' | 'day' | 'datetime-local'
    variant?: 'filled' | 'outlined'
    readonly?: boolean
    unHelperText?: boolean
    helperText?: string
    hidden?: boolean
    /** Chega no `<input>`, não no wrapper — há telas que leem `activeElement.id`. */
    id?: string
    autoComplete?: string
    startAdornment?: ReactNode
    /** Entra antes do botão do calendário, que segue sendo o último da caixa. */
    endAdornment?: ReactNode
}

/** Formato gravado no `datetime-local` — é o do `<input>` nativo que ele substitui. */
const FORMATO_DATA_HORA = 'YYYY-MM-DDTHH:mm'

/**
 * Formato de gravação no formulário, por tipo — igual ao da versão MUI e ao que
 * os schemas Zod esperam: `month` grava `YYYY-MM`, `day` grava só o dia (`DD`),
 * `datetime-local` grava `YYYY-MM-DDTHH:mm` e o restante grava `YYYY-MM-DD`.
 */
function formatarParaFormulario(
    type: FormPickerProps['type'],
    data: Dayjs | null,
) {
    return match([type, data])
        .with(['month', P.nonNullable.select()], value =>
            value.format('YYYY-MM'),
        )
        .with(['day', P.nonNullable.select()], value => value.format('DD'))
        .with(['datetime-local', P.nonNullable.select()], value =>
            value.format(FORMATO_DATA_HORA),
        )
        .with([P.any, P.nonNullable.select()], value =>
            value.format('YYYY-MM-DD'),
        )
        .otherwise(() => null)
}

/** View do `DateField` e formato exibido, por tipo do formulário. */
function exibicao(type: FormPickerProps['type']) {
    return match(type)
        .with('month', () => ({ view: 'month' as const, format: 'MM/YYYY' }))
        .with('datetime-local', () => ({
            view: 'datetime' as const,
            format: 'DD/MM/YYYY HH:mm',
        }))
        .otherwise(() => ({ view: 'date' as const, format: 'DD/MM/YYYY' }))
}

/** Campo de data ligado ao react-hook-form. */
export function FormDatePicker({
    label = 'Competencia',
    name,
    required,
    rules,
    size = 'medium',
    type,
    variant = 'filled',
    disabled: disabledPicker,
    unHelperText,
    helperText,
    readonly,
    hidden,
    id,
    autoComplete,
    startAdornment,
    endAdornment,
}: FormPickerProps) {
    const { control } = useFormContext()
    // `_options` é API interna do RHF — daí o cast.
    const disabled = disabledPicker || (control as any)._options?.disabled
    const { view, format } = exibicao(type)

    return (
        <Controller
            rules={rules}
            name={name}
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
                <DateField
                    label={label}
                    name={name}
                    id={id}
                    // O `FormField` injetava o mesmo `data-testid` — os campos
                    // de data seguem selecionáveis como os de texto.
                    data-testid={name}
                    autoComplete={autoComplete}
                    startAdornment={startAdornment}
                    endAdornment={endAdornment}
                    view={view}
                    format={format}
                    value={value ? dayjs(value) : null}
                    onChange={data =>
                        onChange(formatarParaFormulario(type, data))
                    }
                    size={size}
                    variant={variant}
                    required={required}
                    disabled={disabled}
                    readOnly={readonly}
                    className={hidden ? 'hidden!' : undefined}
                    error={error && (error.message ?? true)}
                    helperText={helperText}
                    unHelperText={unHelperText}
                />
            )}
        />
    )
}
